/**
 * useMatrixFilters Hook
 * Manages filtering, searching, visibility, and ordering for the matrix grid
 *
 * Uses staged memoization to prevent cascading recalculations:
 * 1. Search filtering (debounced)
 * 2. Status filtering
 * 3. Visibility filtering
 * 4. Ordering
 */

import { useState, useMemo, useCallback } from 'react';
import { useDebounce } from './useDebounce';

/**
 * Status filter to effective_status mapping
 */
const STATUS_MAP = {
  'compliant': 'valid',
  'valid': 'valid',
  'expiring': 'expiring',
  'soon': 'expiring',
  'expired': 'expired',
  'missing': 'missing',
  'booked': 'booked',
  'quotes': 'quoting',
  'quoting': 'quoting'
};

/**
 * Custom hook for matrix filtering
 *
 * @param {Array} requirements - Array of requirement objects
 * @param {Array} teamMembers - Array of team member objects
 * @param {Map} assignmentMap - Map of assignments keyed by `${memberId}-${reqId}`
 * @param {Object} options - Configuration options
 * @param {number} options.debounceDelay - Search debounce delay in ms (default: 300)
 * @returns {Object} Filter state and actions
 */
export function useMatrixFilters(requirements, teamMembers, assignmentMap, options = {}) {
  const { debounceDelay = 300 } = options;

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, debounceDelay);

  // Filter state
  const [activeFilters, setActiveFilters] = useState(new Set());

  // Visibility state
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [hiddenRows, setHiddenRows] = useState(new Set());

  // Ordering state
  const [columnOrder, setColumnOrder] = useState([]);
  const [rowOrder, setRowOrder] = useState([]);

  // ============================================
  // Stage 1: Search Filtering (debounced)
  // ============================================
  const searchFilteredData = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return { requirements, teamMembers };
    }

    const tokens = debouncedSearch.toLowerCase().trim().split(/\s+/);

    // Build indexes for faster lookup
    const memberMatches = new Set();
    const reqMatches = new Set();

    // Find matching members
    teamMembers.forEach(member => {
      const nameLower = (member.name || '').toLowerCase();
      const emailLower = (member.email || '').toLowerCase();
      if (tokens.some(t => nameLower.includes(t) || emailLower.includes(t))) {
        memberMatches.add(member.id);
      }
    });

    // Find matching requirements
    requirements.forEach(req => {
      const nameLower = (req.name || '').toLowerCase();
      const descLower = (req.description || '').toLowerCase();
      const categoryLower = (req.category || '').toLowerCase();
      if (tokens.some(t =>
        nameLower.includes(t) ||
        descLower.includes(t) ||
        categoryLower.includes(t)
      )) {
        reqMatches.add(req.id);
      }
    });

    // Apply smart filtering based on what matched
    let filteredMembers = teamMembers;
    let filteredReqs = requirements;

    if (memberMatches.size > 0 && reqMatches.size > 0) {
      // Both matched - show intersection
      filteredMembers = teamMembers.filter(m => memberMatches.has(m.id));
      filteredReqs = requirements.filter(r => reqMatches.has(r.id));
    } else if (memberMatches.size > 0) {
      // Only members matched
      filteredMembers = teamMembers.filter(m => memberMatches.has(m.id));
    } else if (reqMatches.size > 0) {
      // Only requirements matched
      filteredReqs = requirements.filter(r => reqMatches.has(r.id));
    }

    return {
      requirements: filteredReqs,
      teamMembers: filteredMembers
    };
  }, [requirements, teamMembers, debouncedSearch]);

  // ============================================
  // Stage 2: Status Filtering
  // ============================================
  const statusFilteredData = useMemo(() => {
    if (activeFilters.size === 0) {
      return searchFilteredData;
    }

    // Convert filter names to effective_status values
    const targetStatuses = new Set(
      Array.from(activeFilters)
        .map(f => STATUS_MAP[f])
        .filter(Boolean)
    );

    // Check for assignment filters
    const hasAssignedFilter = activeFilters.has('assigned');
    const hasUnassignedFilter = activeFilters.has('unassigned');

    // Filter members who have at least one matching requirement
    const filteredMembers = searchFilteredData.teamMembers.filter(member => {
      return searchFilteredData.requirements.some(req => {
        const key = `${member.id}-${req.id}`;
        const assignment = assignmentMap.get(key);
        if (!assignment) return false;

        // Check status filter (supports both camelCase and snake_case)
        const status = assignment.effectiveStatus || assignment.effective_status;
        const matchesStatus = targetStatuses.size === 0 ||
          targetStatuses.has(status);

        // Check assignment filter
        let matchesAssignment = true;
        if (hasAssignedFilter || hasUnassignedFilter) {
          const isAssigned = assignment.assigned === true;
          matchesAssignment =
            (hasAssignedFilter && isAssigned) ||
            (hasUnassignedFilter && !isAssigned);
        }

        return matchesStatus && matchesAssignment;
      });
    });

    // Filter requirements that have at least one matching member
    const filteredReqs = searchFilteredData.requirements.filter(req => {
      return filteredMembers.some(member => {
        const key = `${member.id}-${req.id}`;
        const assignment = assignmentMap.get(key);
        if (!assignment) return false;

        const reqStatus = assignment.effectiveStatus || assignment.effective_status;
        const matchesStatus = targetStatuses.size === 0 ||
          targetStatuses.has(reqStatus);

        let matchesAssignment = true;
        if (hasAssignedFilter || hasUnassignedFilter) {
          const isAssigned = assignment.assigned === true;
          matchesAssignment =
            (hasAssignedFilter && isAssigned) ||
            (hasUnassignedFilter && !isAssigned);
        }

        return matchesStatus && matchesAssignment;
      });
    });

    return {
      requirements: filteredReqs,
      teamMembers: filteredMembers
    };
  }, [searchFilteredData, activeFilters, assignmentMap]);

  // ============================================
  // Stage 3: Visibility Filtering
  // ============================================
  const visibleData = useMemo(() => ({
    requirements: statusFilteredData.requirements.filter(r => !hiddenColumns.has(r.id)),
    teamMembers: statusFilteredData.teamMembers.filter(m => !hiddenRows.has(m.id))
  }), [statusFilteredData, hiddenColumns, hiddenRows]);

  // ============================================
  // Stage 4: Ordering
  // ============================================
  const orderedData = useMemo(() => {
    let orderedReqs = visibleData.requirements;
    let orderedMembers = visibleData.teamMembers;

    // Apply column ordering if specified
    if (columnOrder.length > 0) {
      const reqMap = new Map(visibleData.requirements.map(r => [r.id, r]));
      orderedReqs = columnOrder
        .map(id => reqMap.get(id))
        .filter(Boolean);

      // Add any requirements not in the order at the end
      const orderedIds = new Set(columnOrder);
      const unorderedReqs = visibleData.requirements.filter(r => !orderedIds.has(r.id));
      orderedReqs = [...orderedReqs, ...unorderedReqs];
    }

    // Apply row ordering if specified
    if (rowOrder.length > 0) {
      const memberMap = new Map(visibleData.teamMembers.map(m => [m.id, m]));
      orderedMembers = rowOrder
        .map(id => memberMap.get(id))
        .filter(Boolean);

      // Add any members not in the order at the end
      const orderedIds = new Set(rowOrder);
      const unorderedMembers = visibleData.teamMembers.filter(m => !orderedIds.has(m.id));
      orderedMembers = [...orderedMembers, ...unorderedMembers];
    }

    return {
      requirements: orderedReqs,
      teamMembers: orderedMembers
    };
  }, [visibleData, columnOrder, rowOrder]);

  // ============================================
  // Actions
  // ============================================
  const toggleFilter = useCallback((filter) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  }, []);

  const setFilter = useCallback((filter, enabled) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (enabled) {
        next.add(filter);
      } else {
        next.delete(filter);
      }
      return next;
    });
  }, []);

  const toggleColumn = useCallback((columnId) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  }, []);

  const toggleRow = useCallback((rowId) => {
    setHiddenRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const showAllColumns = useCallback(() => {
    setHiddenColumns(new Set());
  }, []);

  const showAllRows = useCallback(() => {
    setHiddenRows(new Set());
  }, []);

  const hideAllColumns = useCallback((columnIds) => {
    setHiddenColumns(new Set(columnIds));
  }, []);

  const hideAllRows = useCallback((rowIds) => {
    setHiddenRows(new Set(rowIds));
  }, []);

  const clearActiveFilters = useCallback(() => {
    setActiveFilters(new Set());
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters(new Set());
    setHiddenColumns(new Set());
    setHiddenRows(new Set());
    setSearchInput('');
    // Don't reset columnOrder/rowOrder - preserve data-driven ordering
  }, []);

  const resetToDefaults = useCallback(() => {
    setActiveFilters(new Set());
    setHiddenColumns(new Set());
    setHiddenRows(new Set());
    // Don't reset columnOrder/rowOrder - these represent data ordering
    // and are synced from matrixData. Wiping them causes the hide fields
    // popover to show no options since the sync effect won't re-run
    // unless matrixData itself changes.
  }, []);

  // Apply a saved view's filters
  const applyViewFilters = useCallback((filters) => {
    if (!filters) {
      resetToDefaults();
      return;
    }

    setActiveFilters(new Set(filters.activeFilters || []));
    setHiddenColumns(new Set(filters.hiddenColumns || []));
    setHiddenRows(new Set(filters.hiddenRows || []));
    // Only override ordering if the saved view actually has it,
    // otherwise preserve the current data-driven order
    if (filters.columnOrder?.length > 0) setColumnOrder(filters.columnOrder);
    if (filters.rowOrder?.length > 0) setRowOrder(filters.rowOrder);
  }, [resetToDefaults]);

  // Get current filters as a serializable object
  const getCurrentFilters = useCallback(() => ({
    activeFilters: Array.from(activeFilters),
    hiddenColumns: Array.from(hiddenColumns),
    hiddenRows: Array.from(hiddenRows),
    columnOrder,
    rowOrder
  }), [activeFilters, hiddenColumns, hiddenRows, columnOrder, rowOrder]);

  // Check if current filters differ from a saved state
  const hasChangesFrom = useCallback((savedFilters) => {
    if (!savedFilters) {
      // For the "All" view (no saved filters), only user-applied filters count as changes
      // columnOrder/rowOrder always have values from data sync, so exclude them
      return activeFilters.size > 0 ||
             hiddenColumns.size > 0 ||
             hiddenRows.size > 0;
    }

    const currentFilters = getCurrentFilters();
    return JSON.stringify(currentFilters) !== JSON.stringify(savedFilters);
  }, [activeFilters, hiddenColumns, hiddenRows, columnOrder, rowOrder, getCurrentFilters]);

  return {
    // State
    searchInput,
    debouncedSearch,
    activeFilters,
    hiddenColumns,
    hiddenRows,
    columnOrder,
    rowOrder,

    // Filtered data (final output)
    filteredRequirements: orderedData.requirements,
    filteredTeamMembers: orderedData.teamMembers,

    // Intermediate stages (for debugging/display)
    searchFilteredCount: {
      requirements: searchFilteredData.requirements.length,
      teamMembers: searchFilteredData.teamMembers.length
    },
    statusFilteredCount: {
      requirements: statusFilteredData.requirements.length,
      teamMembers: statusFilteredData.teamMembers.length
    },

    // Actions
    setSearchInput,
    toggleFilter,
    setFilter,
    toggleColumn,
    toggleRow,
    showAllColumns,
    showAllRows,
    hideAllColumns,
    hideAllRows,
    clearActiveFilters,
    setColumnOrder,
    setRowOrder,
    clearFilters,
    resetToDefaults,
    applyViewFilters,
    getCurrentFilters,
    hasChangesFrom
  };
}

export default useMatrixFilters;
